import { ReactNode, useEffect } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import SupplierSelectPopover from "components/form_components/SupplierSelectPopover";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import EllipsisText from "components/ui/tooltip/EllipsisText";
import { UseBidSupplierBuyerNoticeSummary } from "lib/hooks/api/bid/bids/useBid";
import { UseLeadSupplierBuyerNoticeSummary } from "lib/hooks/api/leads/useLead";
import { UIcon } from "lib/icons/untitled_ui/UIcon";
import Chart from "./Chart";
import { ChartData, ChartType, OrgDetails } from "./types";
import { CARD_ICON_COLORS, ChartConfigType, getChartConfig, getChartData } from "./utils";

export default function ChartCard({
  title,
  chartType,
  objectId,
  similarNoticeIds,
  incumbentSupplierIds,
  defaultBuyer,
  defaultSupplierIds,
  emptyState,
  isLoading,
  queryProvider,
}: {
  title: ReactNode;
  chartType: ChartType;
  objectId: string;
  similarNoticeIds: string[];
  incumbentSupplierIds: string[];
  defaultBuyer: OrgDetails;
  defaultSupplierIds: string[];
  emptyState: JSX.Element | null;
  isLoading: boolean;
  queryProvider: UseLeadSupplierBuyerNoticeSummary | UseBidSupplierBuyerNoticeSummary;
}) {
  const { control, watch, formState, reset } = useForm<{ supplierIds: string[] }>({
    defaultValues: { supplierIds: [] },
  });

  const { supplierIds } = watch();
  const hasEditedSuppliers = formState.isDirty;

  const { data, isLoading: isSummaryLoading } = queryProvider(objectId, {
    suppliers: hasEditedSuppliers ? supplierIds : defaultSupplierIds,
  });

  useEffect(() => {
    if (supplierIds.length === 0 && !hasEditedSuppliers) {
      const filteredSupplierIds =
        data
          ?.filter(
            (summary) => summary.allAwards.totalCount > 0 || summary.similarAwards.totalCount > 0,
          )
          .map((summary) => summary.supplier.id) ?? [];

      if (!filteredSupplierIds.length) {
        return;
      }

      reset({ supplierIds: filteredSupplierIds });
    }
  }, [reset, data, supplierIds, hasEditedSuppliers]);

  const includeEmptyResults = hasEditedSuppliers ? true : false;
  const chartData = getChartData(data ?? [], includeEmptyResults);
  const chartConfig = getChartConfig(chartType);

  return (
    <Card>
      <Header justifyContent="space-between">
        <Title>
          <UIcon icon="presentationChart02" color={CARD_ICON_COLORS[chartType]} size={18} />
          {typeof title === "string" ? <EllipsisText h3>{title}</EllipsisText> : title}
        </Title>
        <SupplierSelectPopover
          title="Suppliers"
          icon="building07"
          label=""
          name="supplierIds"
          control={control}
          categoryOptions={[chartType === "partners" ? "Partner" : "Competitor"]}
          incumbentSuppliers={incumbentSupplierIds}
        />
      </Header>
      <Content>
        {isLoading ? (
          <SkeletonContainer>
            <SkeletonText width="100%" customSpacing fontSize={135} variant="rectangular" />
          </SkeletonContainer>
        ) : (
          <ChartContainer
            chartType={chartType}
            chartData={chartData}
            selectedBuyer={defaultBuyer}
            similarNoticeIds={similarNoticeIds}
            isLoading={isSummaryLoading}
            chartConfig={chartConfig}
            emptyState={!hasEditedSuppliers ? emptyState : null}
          />
        )}
      </Content>
    </Card>
  );
}

function ChartContainer({
  chartType,
  chartData,
  selectedBuyer,
  similarNoticeIds,
  chartConfig,
  isLoading,
  emptyState,
}: {
  chartType: ChartType;
  chartData: ChartData[] | null;
  selectedBuyer: OrgDetails;
  similarNoticeIds: string[];
  isLoading?: boolean;
  chartConfig: ChartConfigType;
  emptyState: JSX.Element | null;
}): JSX.Element | null {
  if (emptyState) {
    return <Flex justifyContent="center">{emptyState}</Flex>;
  }

  if (isLoading) {
    return (
      <SkeletonContainer>
        <SkeletonText width="100%" customSpacing fontSize={60} variant="rectangular" />
        <SkeletonText width="100%" customSpacing fontSize={60} variant="rectangular" />
        <SkeletonText width="100%" customSpacing fontSize={60} variant="rectangular" />
      </SkeletonContainer>
    );
  }

  return (
    <Chart
      chartType={chartType}
      chartData={chartData ?? []}
      chartConfig={chartConfig}
      selectedBuyer={selectedBuyer}
      similarNoticeIds={similarNoticeIds}
    />
  );
}

const Card = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  width: "100%",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: "8px",
}));

const Header = styled(Flex)(({ theme }) => ({
  gap: 8,
  alignItems: "center",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  padding: 24,
}));

const Content = styled(Flex)({
  flexDirection: "column",
  padding: "24px 24px 40px 0px",
});

const SkeletonContainer = styled(Flex)({
  flexDirection: "column",
  gap: 8,
  padding: "24px",
});

const Title = styled(Flex)({
  minWidth: 0,
  flex: 1,
  gap: 8,
  alignItems: "center",
});
